import { Link } from "react-router-dom";
import { timeAgo } from "@/lib/api";
import { StatusChip } from "./RegulationRadar";
import { sectorTrack } from "./SectorHero";

export const SUBTHEME_COLOR: Record<string, string> = {
  ENFORCEMENT: "#F87171", COURTS: "#F87171", SANCTIONS: "#F87171",
  LEGISLATION: "#A78BFA", RULEMAKING: "#00E5FF", "REGULATORY ACTION": "#00E5FF",
  STABLECOINS: "#34D399", LICENSING: "#60A5FA", "MARKET STRUCTURE": "#60A5FA",
  CUSTODY: "#60A5FA", AML: "#F59E0B", TAX: "#F59E0B", COMPLIANCE: "#60A5FA",
  JURISDICTION: "#A78BFA",
};

export const SubthemeChip = ({ s }: { s: string }) => {
  const c = SUBTHEME_COLOR[s] || "#9CA3AF";
  return (
    <span className="inline-flex rounded border px-1.5 py-0.5 font-mono text-[8.5px] font-bold tracking-[0.14em] shrink-0"
      style={{ borderColor: `${c}55`, backgroundColor: `${c}14`, color: c }}>
      {s}
    </span>
  );
};

const PolicyHead = ({ title, desc, testid, chip }: { title: string; desc: string; testid: string; chip?: string }) => (
  <>
    <div className="flex items-center gap-2.5 mb-1.5" data-testid={testid}>
      <span aria-hidden className="h-3.5 w-[3px] rounded-full bg-[#A78BFA]" />
      <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#A78BFA]">{title}</h2>
      {chip && (
        <span className="rounded border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-1.5 py-0.5 font-mono text-[8px] font-bold tracking-[0.18em] text-[#00E5FF]">
          {chip}
        </span>
      )}
    </div>
    <p className="text-gray-500 text-[13px] mb-4">{desc}</p>
  </>
);

export { PolicyHead };

const WhatChanged = ({ items, window }: { items: any[]; window: number }) => (
  <div data-testid="policy-what-changed"
    className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4 sm:p-5">
    <div className="flex items-baseline justify-between gap-3">
      <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.26em] text-white">What Changed</p>
      <p className="font-mono text-[8px] uppercase tracking-wide text-gray-600">Last {window}h · significance-ranked</p>
    </div>
    <div className="divide-y divide-white/[0.06] mt-2">
      {items.map((it, i) => (
        <Link key={it.slug} to={`/article/${it.slug}`} data-testid={`wc-item-${i}`}
          onClick={() => sectorTrack("intelligence_click", it.slug, "policy")}
          className="group flex gap-3.5 py-3 first:pt-1.5 last:pb-1">
          <span aria-hidden className="font-mono text-[11px] font-bold text-[#A78BFA]/60 pt-0.5 shrink-0 w-5">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2 flex-wrap">
              <SubthemeChip s={it.subtheme} />
              <span className="font-mono text-[8.5px] uppercase tracking-wide text-gray-600">{timeAgo(it.published_at)}</span>
            </span>
            <span className="block font-news font-bold text-white text-[14.5px] leading-snug mt-1.5 group-hover:text-[#A78BFA] transition-colors">
              {it.title}
            </span>
            <span className="block text-gray-500 text-[11.5px] leading-snug mt-1">{it.why}</span>
          </span>
        </Link>
      ))}
    </div>
  </div>
);

const Signals = ({ items }: { items: any[] }) => (
  <div data-testid="policy-signals" className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4">
    <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.26em] text-[#00E5FF]">Policy Signals</p>
    <div className="space-y-3 mt-3">
      {items.map((s, i) => (
        <div key={i} className="flex gap-2.5">
          <span aria-hidden className="mt-[5px] h-1.5 w-1.5 rounded-full bg-[#00E5FF]/70 shrink-0" />
          <div className="min-w-0">
            <p className="text-gray-200 text-[12.5px] leading-snug">{s.text}</p>
            <p className="font-mono text-[8px] uppercase tracking-wide text-gray-600 mt-0.5">{s.basis}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EffectiveDates = ({ items }: { items: any[] }) => (
  <div data-testid="policy-effective-dates" className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4">
    <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.26em] text-[#34D399]">Effective Dates</p>
    <p className="font-mono text-[8px] uppercase tracking-wide text-gray-600 mt-1">Structured record dates only</p>
    <div className="divide-y divide-white/[0.06] mt-1.5">
      {items.map((e) => (
        <Link key={e.slug} to={`/regulation/${e.slug}`}
          onClick={() => sectorTrack("regulation_click", e.slug, "policy")}
          className="group flex items-center gap-3 py-2.5 last:pb-0">
          <span className="font-mono text-[10.5px] font-bold text-white shrink-0">{e.date}</span>
          <span className="min-w-0 flex-1 text-gray-300 text-[12px] leading-snug truncate group-hover:text-white transition-colors">
            {e.regulation}
          </span>
          <span className="font-mono text-[8px] font-bold uppercase tracking-wide shrink-0 text-gray-500">
            {e.in_force ? "In force" : "Upcoming"}
          </span>
        </Link>
      ))}
    </div>
  </div>
);

const WhyItMatters = ({ items }: { items: any[] }) => (
  <div className="mt-4" data-testid="policy-why-matters">
    <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.26em] text-white mb-3">Why It Matters</p>
    <div className={`grid gap-3 sm:grid-cols-2 ${items.length >= 4 ? "lg:grid-cols-4" : items.length === 3 ? "lg:grid-cols-3" : ""}`}>
      {items.map((w) => (
        <Link key={w.slug} to={`/regulation/${w.slug}`}
          onClick={() => sectorTrack("regulation_click", w.slug, "policy")}
          className="group flex flex-col rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 hover:border-[#A78BFA]/40 transition-colors">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[8.5px] font-bold uppercase tracking-[0.2em] text-[#A78BFA]">{w.area}</span>
            <span className="ml-auto"><StatusChip status={w.status} /></span>
          </div>
          <p className="font-news font-bold text-white text-[13.5px] leading-snug mt-2 line-clamp-2 group-hover:text-[#A78BFA] transition-colors">
            {w.regulation}
          </p>
          <p className="text-gray-400 text-[11.5px] leading-relaxed mt-2 line-clamp-4">{w.text}</p>
        </Link>
      ))}
    </div>
  </div>
);

/* POLICY INTELLIGENCE — signature editorial/data hybrid (§8-10). Every block is
   evidence-backed: What Changed = family+quality+significance-gated stories;
   Why It Matters = structured records' editorial analysis; dates = stored dates;
   signals = deterministic counts. Blocks hide independently when data is absent. */
export const PolicyIntelligence = ({ data }: { data: any }) => {
  if (!data) return null;
  const wc = data.what_changed || [];
  const wm = data.why_it_matters || [];
  const signals = data.signals || [];
  const eff = data.effective_dates || [];
  if (!wc.length && !wm.length) return null;
  return (
    <section data-testid="policy-intelligence" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12">
      <PolicyHead title="Policy Intelligence" chip="Evidence-backed"
        desc="What changed, why it matters and the signals inside validated NewsLayer policy coverage." testid="policy-intelligence-head" />
      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        {wc.length > 0 && <WhatChanged items={wc} window={data.window_hours} />}
        <div className="space-y-4 min-w-0">
          {signals.length > 0 && <Signals items={signals} />}
          {eff.length > 0 && <EffectiveDates items={eff} />}
        </div>
      </div>
      {wm.length > 0 && <WhyItMatters items={wm} />}
      <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-gray-600 mt-4 leading-relaxed">
        {data.provenance}
      </p>
    </section>
  );
};
